import { useEffect, useState } from 'react';
import { KrDetailSkeleton } from '@workboard/ui-okr';
import { Typography, ThemeProvider } from '@mui/material';

import { useTranslation } from 'react-i18next';
import Chevron from '../../images/dropdown-18-px.svg';
import { Box, Flex } from '../ui';
import OkrService from '../../services/okrs';
import { ViewMoreButton } from './ViewMoreButton';
import { TeamKeyResultCardList } from './TeamKeyResultCardList';
import { GlobalStyle, theme } from './../../../../components/theme/theme';
import { MeetingOkrsTooltip } from './MeetingOkrsTooltip';

export const LIMIT_PAGE = 10;
export const MeetingKeyResultsSection = ({
  meetingId,
  loadingDirectReportee,
  directReporteeId,
  open = false,
  isPrint = false,
  isOneOnOne = false
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(open);
  const [isLoading, setIsLoading] = useState(true);
  const [rawKeyResults, setRawKeyResults] = useState([]);
  const [keyResults, setKeyResults] = useState([]);
  const [initIndexEnd, setInitIndexEnd] = useState(0);

  const showMore = rawKeyResults.length > initIndexEnd && !isPrint;
  const noData = !rawKeyResults.length;
  const wrapperStyle = isPrint ? 'm-print-sections-wrapper' : 'm-details-extra-details';

  const handleToggle = () => setOpen(!open);

  const handleFetchData = async () => {
    if (!directReporteeId) return;
    let err, data;
    if (!isOneOnOne) {
      const params = { meetingId, attendeeId: directReporteeId, excludePersonalKrs: true };
      [err, data] = await OkrService.getMeetingKeyResults(params);
    } else {
      [err, data] = await OkrService.getKeyResultsByUserId({ userId: directReporteeId });
    }

    if (!err) {
      const totalCount = data.length;
      let sliceIndexEnd = totalCount > LIMIT_PAGE ? LIMIT_PAGE : totalCount;
      const krSlice = isPrint ? data : data.slice(0, sliceIndexEnd)
      setRawKeyResults(data);
      setKeyResults(krSlice);
      setInitIndexEnd(sliceIndexEnd);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!loadingDirectReportee) handleFetchData();
  }, [loadingDirectReportee, directReporteeId]);

  if(isPrint && !isLoading && !keyResults.length) return null;

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Box className={wrapperStyle} style={{ marginTop: '7px' }}>
        <Flex
          className="m-unexpanded-details"
          onClick={handleToggle}
          style={{ borderRadius: open ? '4px 4px 0 0' : '4px' }}
        >
          <Box>
            <span className="m-extra-details-text">{t('Person’s Key Results on Team Objectives')}</span>
            {!isOneOnOne && !isPrint && (<MeetingOkrsTooltip/>)}
          </Box>
          {!isPrint && <Box className="m-extra-details-chevron">
            <Chevron className="m-chevron" style={{ transform: open ? 'rotate(0deg)' : 'rotate(-180deg)' }} />
          </Box>}
        </Flex>
        {open && isLoading && (
          <Flex className="m-expanded-details">
            <Box style={{ flexGrow: 4 }}>
              <KrDetailSkeleton />
              <KrDetailSkeleton />
            </Box>
          </Flex>
        )}
        {open && !isLoading && noData && (
          <Flex className="m-expanded-details">
            <Box style={{ flexGrow: 4, paddingLef: 5, marginRight: -15, paddingBottom: 5 }}>
              <Typography variant="subtitle1">{t('No Open Key Results')}</Typography>
            </Box>
          </Flex>
        )}
        {open && (!isLoading || !loadingDirectReportee) && (
          <Flex className="m-expanded-details">
            <Box style={{ flexGrow: 4, paddingLef: 5, marginRight: -15 }}>
              <TeamKeyResultCardList isLoading={isLoading} data={keyResults} />
              {showMore && (
                <ViewMoreButton
                  label={t('Key Results')}
                  rawData={rawKeyResults}
                  pageData={keyResults}
                  indexEnd={initIndexEnd}
                  updateViewData={setKeyResults}
                />
              )}
            </Box>
          </Flex>
        )}
      </Box>
    </ThemeProvider>
  );
};
